import { Routes } from '@angular/router';

import { authGuard } from './core/auth/auth.guard';
import { guestGuard } from './core/auth/guest.guard';
import { paymentReturnGuard } from './core/guards/payment-return.guard';
import { profileCompletionGuard } from './core/auth/profile-completion.guard';
import { roleGuard } from './core/auth/role.guard';
import { roleSelectionGuard } from './core/auth/role-selection.guard';
import { EDUCATOR_ROLES, SHOW_ADMIN_ROLES } from './models/common/user-role.type';

export const routes: Routes = [

  // Landing Page. paymentReturnGuard intercepts the card gateway's hand-back,
  // which the `**` route funnels here, and forwards it to the cart.
  {
    path: '',
    canActivate: [paymentReturnGuard],
    loadChildren: () =>
      import('./features/landing/landing.module')
        .then(m => m.LandingModule)
  },

  // Legacy aliases: auth routes now live under the family namespace.
  {
    path: 'login',
    redirectTo: 'family/login',
    pathMatch: 'full'
  },
  {
    path: 'register',
    redirectTo: 'family/register',
    pathMatch: 'full'
  },

  // Family login (authenticated users are redirected to their portal by guestGuard)
  {
    path: 'family/login',
    canActivate: [guestGuard, roleSelectionGuard],
    data: { portal: 'family' },
    loadChildren: () =>
      import('./features/auth/login.module')
        .then(m => m.LoginModule)
  },

  // Educator login. No guestGuard: educators serve multiple orgs, so an already
  // signed-in educator must still land here to pick which org portal to enter.
  {
    path: 'educator/login',
    canActivate: [roleSelectionGuard],
    data: { portal: 'educator' },
    loadChildren: () =>
      import('./features/auth/login.module')
        .then(m => m.LoginModule)
  },

  // District Educator login. No guestGuard: a district agent's role maps to the
  // educator portal, so it would bounce them into /educator instead.
  {
    path: 'districteducator/login',
    canActivate: [roleSelectionGuard],
    data: { portal: 'districtEducator' },
    loadChildren: () =>
      import('./features/auth/login.module')
        .then(m => m.LoginModule)
  },

  // Show-admin login. OTP-based like educator/family, so it needs a roleId —
  // roleGuard also sends expired admin sessions straight here.
  {
    path: 'show-admin/login',
    canActivate: [guestGuard, roleSelectionGuard],
    data: { portal: 'showAdmin' },
    loadChildren: () =>
      import('./features/auth/login.module')
        .then(m => m.LoginModule)
  },

  // Register a new family. Public: no roleSelectionGuard, because the page is
  // reachable straight from a shared link with no landing-page visit behind it,
  // and it resolves the family roleId from the roles API itself.
  {
    path: 'family/register',
    canActivate: [guestGuard],
    data: { portal: 'family' },
    loadChildren: () =>
      import('./features/auth/register.module')
        .then(m => m.RegisterModule)
  },

  // Email verification landing (from the registration email link).
  // No guard: the link must work whether or not a session exists.
  // `exhibitor/verify-email` is an alias for links the backend still emits
  // with the legacy path; the canonical route is `verify-email`.
  {
    path: 'verify-email',
    loadChildren: () =>
      import('./features/auth/verify-email.module')
        .then(m => m.VerifyEmailModule)
  },
  {
    path: 'exhibitor/verify-email',
    loadChildren: () =>
      import('./features/auth/verify-email.module')
        .then(m => m.VerifyEmailModule)
  },

  // Family Portal (requires login and profile completion)
  {
    path: 'family',
    canActivate: [authGuard, profileCompletionGuard],
    loadChildren: () =>
      import('./features/family/family.module')
        .then(m => m.FamilyModule)
  },

  // Educator Portal (Ag Teachers + 4-H County/District Agents)
  {
    path: 'educator',
    canActivate: [roleGuard(EDUCATOR_ROLES)],
    loadChildren: () =>
      import('./features/educator/educator.module')
        .then(m => m.EducatorModule)
  },

  // Show-Admin Portal
  {
    path: 'show-admin',
    canActivate: [roleGuard(SHOW_ADMIN_ROLES)],
    loadChildren: () =>
      import('./features/show-admin/show-admin.module')
        .then(m => m.ShowAdminModule)
  },

  {
    path: '**',
    redirectTo: ''
  }
];